import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { lastValueFrom, RmsMessageService, RmsPulldownService } from '@services';
import { take } from 'rxjs/operators';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { FuturesDataService, FuturesNotification, FuturesPosition } from '../../../../services/futures-data.service';
import { downloadCsv } from '../../../../services/swap-data.service';
import { FuturesDetailComponent } from './futures-detail/futures-detail.component';
import { FuturesContractFormComponent } from './futures-contract-form/futures-contract-form.component';
import { PositionRow, daysUntil, fndClass } from './futures-shared';

// a reduced position isn't a whole number of the exchange's contracts (e.g. 11,250 lbs on ICE)
function isWholeContracts(weight: number, contractWeight: number): boolean {
  const lots = weight / contractWeight;
  return Math.abs(lots - Math.round(lots)) < 0.0001;
}

// RMS contracts: partial once some weight is applied to reserves; legacy: not whole contracts
function positionStatus(p: FuturesPosition, weight: number, contractWeight: number): 'Open' | 'Partial' | 'Closed' {
  if (!p.IsActive) return 'Closed';
  if (p.Source === 'RMS') return Number(p.AllocatedWeightLbs) > 0 ? 'Partial' : 'Open';
  return isWholeContracts(weight, contractWeight) ? 'Open' : 'Partial';
}

const round2 = (n: number) => Math.round(n * 100) / 100;

// date-only values arrive as '2026-11-19T00:00:00.000Z'; drop the Z so they display as that calendar day
const localDate = (d: string | null) => d ? d.substring(0, 10) + 'T00:00:00' : null;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface CustomerSummaryRow {
  CompanyID: number;
  Customer: string;
  Positions: number;
  Contracts: number;
  WeightLbs: number;
  FuturesMonths: number;
  NextFND: string | null;
}

interface MonthSummaryRow {
  FutureCompositeID: number | null;
  FuturesLabel: string;
  FuturesYear: number | null;
  FND: string | null;
  DaysToFND: number | null;
  Customers: number;
  Positions: number;
  Contracts: number;
  WeightLbs: number;
}

@Component({
  selector: 'app-futures-management',
  imports: [RmsModule, FuturesDetailComponent],
  templateUrl: './futures-management.component.html',
  styleUrl: './futures-management.component.scss'
})
export class FuturesManagementComponent implements OnInit {
  // rms-table needs columns before it renders
  Loaded = false;
  IncludeClosed = false;

  AllRows: PositionRow[] = [];
  PositionRows: PositionRow[] = [];
  ContractFilter: string | null = null;
  ContractCounts: { name: string; count: number }[] = [];
  CustomerRows: CustomerSummaryRow[] = [];
  MonthRows: MonthSummaryRow[] = [];

  SelectedPosition: PositionRow | null = null;
  SelectedReserveLines: PositionRow[] = [];

  SelectedCustomer: CustomerSummaryRow | null = null;
  SelectedCustomerMonths: MonthSummaryRow[] = [];

  RiskNotifications: FuturesNotification[] = [];
  RiskRows: FuturesNotification[] = [];
  RiskFilter: string | null = null;
  RiskCounts = { Delivered: 0, Pending: 0, Failed: 0 };
  RiskColumns: Column[] = [];

  PositionColumns: Column[] = [];
  CustomerColumns: Column[] = [];
  MonthColumns: Column[] = [];
  CustomerMonthColumns: Column[] = [];

  Stats = { Positions: 0, Partial: 0, PartialWeightLbs: 0, Contracts: 0, WeightLbs: 0, Customers: 0, NextFND: null as string | null, NextFNDLabel: '' };

  Buttons: HeaderButton[] = [
    { label: 'Export (CSV)', onAction: () => this.exportPositions() },
    { label: 'Audit Report (CSV)', onAction: () => this.exportAuditReport() },
    { label: '+ New Futures Contract', class: 'btn-bg-green', onAction: () => this.openNewContract() }
  ];

  constructor(
    private futuresData: FuturesDataService,
    private msg: RmsMessageService,
    private pulldownService: RmsPulldownService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    this.PositionColumns = this.createPositionColumns();
    this.CustomerColumns = this.createCustomerColumns();
    this.MonthColumns = this.createMonthColumns(true);
    this.CustomerMonthColumns = this.createMonthColumns(false);
    this.RiskColumns = this.createRiskColumns();
    await this.loadPositions();
    this.Loaded = true;
    this.cdr.markForCheck();
  }

  async loadPositions() {
    try {
      const [rows, notifications] = await Promise.all([
        lastValueFrom(this.futuresData.getPositions(this.IncludeClosed)),
        lastValueFrom(this.futuresData.getRiskNotifications())
      ]);
      this.AllRows = (rows ?? []).map(r => this.toRow(r));
      this.RiskNotifications = notifications ?? [];
      this.RiskCounts = {
        Delivered: this.RiskNotifications.filter(n => n.DeliveryStatus === 'Delivered').length,
        Pending: this.RiskNotifications.filter(n => n.DeliveryStatus === 'Pending').length,
        Failed: this.RiskNotifications.filter(n => n.DeliveryStatus === 'Failed').length
      };
      this.applyRiskFilter();
      this.buildSummaries();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load futures positions.');
    }
    this.cdr.markForCheck();
  }

  async toggleIncludeClosed() {
    this.IncludeClosed = !this.IncludeClosed;
    await this.loadPositions();
  }

  private toRow(p: FuturesPosition): PositionRow {
    const monthName = p.FuturesMonth ? MONTH_NAMES[p.FuturesMonth - 1] : '';
    const weight = Number(p.WeightLbs) || 0;
    const contractWeight = Number(p.ContractWeight) || 37500;
    return {
      ...p,
      FND: localDate(p.FND),
      PurchaseDate: localDate(p.PurchaseDate),
      ContractStartDate: localDate(p.ContractStartDate),
      ContractEndDate: localDate(p.ContractEndDate),
      WeightLbs: round2(weight),
      FuturesMonthName: monthName,
      FuturesLabel: monthName && p.FuturesYear ? `${monthName} ${p.FuturesYear}` : (p.FutureCompositeName ?? 'Not set'),
      ContractValue: p.Price != null ? Number(p.Price) * weight : null,
      DaysToFND: daysUntil(p.FND),
      PositionStatus: positionStatus(p, weight, contractWeight),
      ApplicationDisplay: p.Source === 'RMS'
        ? (Number(p.AllocatedWeightLbs) > 0 ? `${Number(p.AllocatedWeightLbs).toLocaleString('en-US')} lbs applied` : 'Not applied yet')
        : (p.ApplicationStatus ?? (p.IsActive ? 'Awaiting application' : '—')),
      Reference: p.ReserveOrderID != null ? `Reserve ${p.ReserveOrderID}` : `FC-${p.FuturesPositionID}`,
      FileRef: p.ReserveOrderID != null ? String(p.ReserveOrderID) : `FC-${p.FuturesPositionID}`
    };
  }

  private buildSummaries() {
    const statuses = ['Draft', 'Generated', 'Sent', 'Pending Signature', 'Signed', 'Expired'];
    this.ContractCounts = statuses.map(name => ({ name, count: this.AllRows.filter(r => r.ContractStatusName === name).length }));
    this.applyContractFilter();

    const open = this.AllRows.filter(r => r.IsActive);

    const upcoming = open
      .filter(r => r.DaysToFND !== null && r.DaysToFND >= 0)
      .sort((a, b) => (a.DaysToFND! - b.DaysToFND!))[0];

    this.Stats = {
      Positions: open.length,
      Partial: open.filter(r => r.PositionStatus === 'Partial').length,
      PartialWeightLbs: open.filter(r => r.PositionStatus === 'Partial').reduce((s, r) => round2(s + r.WeightLbs), 0),
      Contracts: open.reduce((s, r) => s + r.Contracts, 0),
      WeightLbs: open.reduce((s, r) => round2(s + r.WeightLbs), 0),
      Customers: new Set(open.map(r => r.CompanyID)).size,
      NextFND: upcoming?.FND ?? null,
      NextFNDLabel: upcoming ? `${upcoming.FuturesLabel} · ${upcoming.DaysToFND} days` : ''
    };

    this.CustomerRows = this.groupByCustomer(open);
    this.MonthRows = this.groupByMonth(open);

    if (this.SelectedCustomer) {
      const still = this.CustomerRows.find(c => c.CompanyID === this.SelectedCustomer!.CompanyID) ?? null;
      this.selectCustomer(still);
    }
  }

  private groupByCustomer(rows: PositionRow[]): CustomerSummaryRow[] {
    const map = new Map<number, PositionRow[]>();
    rows.forEach(r => map.set(r.CompanyID, [...(map.get(r.CompanyID) ?? []), r]));

    return [...map.values()].map(list => {
      const fnds = list.filter(r => r.DaysToFND !== null && r.DaysToFND >= 0)
        .sort((a, b) => a.DaysToFND! - b.DaysToFND!);
      return {
        CompanyID: list[0].CompanyID,
        Customer: list[0].Customer,
        Positions: list.length,
        Contracts: list.reduce((s, r) => s + r.Contracts, 0),
        WeightLbs: list.reduce((s, r) => round2(s + r.WeightLbs), 0),
        FuturesMonths: new Set(list.map(r => r.FutureCompositeID)).size,
        NextFND: fnds[0]?.FND ?? null
      };
    });
  }

  private groupByMonth(rows: PositionRow[]): MonthSummaryRow[] {
    const map = new Map<number | null, PositionRow[]>();
    rows.forEach(r => map.set(r.FutureCompositeID, [...(map.get(r.FutureCompositeID) ?? []), r]));

    return [...map.values()]
      .map(list => ({
        FutureCompositeID: list[0].FutureCompositeID,
        FuturesLabel: list[0].FuturesLabel,
        FuturesYear: list[0].FuturesYear,
        FND: list[0].FND,
        DaysToFND: list[0].DaysToFND,
        Customers: new Set(list.map(r => r.CompanyID)).size,
        Positions: list.length,
        Contracts: list.reduce((s, r) => s + r.Contracts, 0),
        WeightLbs: list.reduce((s, r) => round2(s + r.WeightLbs), 0)
      }))
      .sort((a, b) => (a.FND ?? '9999').localeCompare(b.FND ?? '9999'));
  }

  selectCustomer(row: CustomerSummaryRow | null) {
    this.SelectedCustomer = row;
    this.SelectedCustomerMonths = row
      ? this.groupByMonth(this.AllRows.filter(r => r.IsActive && r.CompanyID === row.CompanyID))
      : [];
    this.cdr.markForCheck();
  }

  createPositionColumns(): Column[] {
    return [
      { key: 'Reference', label: 'Reference', hidden: false },
      { key: 'Customer', label: 'Customer', hidden: false },
      { key: 'FuturesMonthName', label: 'Futures Month', type: 'select', SearchType: 'select', hidden: false },
      { key: 'FuturesYear', label: 'Futures Year', type: 'select', SearchType: 'select', hidden: false },
      { key: 'Contracts', label: 'Contracts', type: 'number', fixed: 2, hidden: false },
      { key: 'WeightLbs', label: 'Weight (lbs)', type: 'number', hidden: false },
      { key: 'Price', label: 'Price / lb', type: 'number', fixed: 4, hidden: false },
      { key: 'FND', label: 'First Notice Day', type: 'date', SearchType: 'date', hidden: false, cellClass: (row) => fndClass(row.DaysToFND) },
      { key: 'DaysToFND', label: 'Days to FND', type: 'number', hidden: false, cellClass: (row) => fndClass(row.DaysToFND) },
      { key: 'PositionStatus', label: 'Position', type: 'select', SearchType: 'select', hidden: false },
      { key: 'ContractStatusName', label: 'Contract', type: 'select', SearchType: 'select', hidden: false },
      { key: 'PriceUnit', label: 'Unit', type: 'select', SearchType: 'select', hidden: false },
      { key: 'TraderName', label: 'Trader', type: 'select', SearchType: 'select', hidden: false },
      { key: 'PurchaseDate', label: 'Purchase Date', type: 'date', SearchType: 'date', hidden: false },
      { key: 'ExchangeName', label: 'Exchange', type: 'select', SearchType: 'select', hidden: true },
      { key: 'ContractValue', label: 'Contract Value', type: 'currency', hidden: true },
      { key: 'InventoryID', label: 'Lot #', type: 'number', hidden: true },
      { key: 'ContractEndDate', label: 'Contract End', type: 'date', SearchType: 'date', hidden: true },
      { key: 'ApplicationDisplay', label: 'Applied to Reserves', type: 'select', SearchType: 'select', hidden: false },
      { key: 'TerminalAllocation', label: 'Terminal', hidden: true },
    ];
  }

  createCustomerColumns(): Column[] {
    return [
      { key: 'Customer', label: 'Customer', hidden: false, clickable: true, onActionKey: 'selectCustomer', onAction: (row) => this.selectCustomer(row) },
      { key: 'Positions', label: 'Positions', type: 'number', hidden: false },
      { key: 'Contracts', label: 'Contracts', type: 'number', fixed: 2, hidden: false },
      { key: 'WeightLbs', label: 'Open Weight (lbs)', type: 'number', hidden: false },
      { key: 'FuturesMonths', label: 'Futures Months', type: 'number', hidden: false },
      { key: 'NextFND', label: 'Next FND', type: 'date', SearchType: 'date', hidden: false, cellClass: (row) => fndClass(daysUntil(row.NextFND)) },
    ];
  }

  createMonthColumns(showCustomers: boolean): Column[] {
    return [
      { key: 'FuturesLabel', label: 'Futures Month', hidden: false },
      { key: 'FuturesYear', label: 'Year', type: 'select', SearchType: 'select', hidden: false },
      { key: 'FND', label: 'First Notice Day', type: 'date', SearchType: 'date', hidden: false, cellClass: (row) => fndClass(row.DaysToFND) },
      { key: 'DaysToFND', label: 'Days to FND', type: 'number', hidden: false, cellClass: (row) => fndClass(row.DaysToFND) },
      { key: 'Customers', label: 'Customers', type: 'number', hidden: !showCustomers },
      { key: 'Positions', label: 'Positions', type: 'number', hidden: false },
      { key: 'Contracts', label: 'Contracts', type: 'number', fixed: 2, hidden: false },
      { key: 'WeightLbs', label: 'Weight (lbs)', type: 'number', hidden: false },
    ];
  }

  selectRiskFilter(status: string | null) {
    this.RiskFilter = this.RiskFilter === status ? null : status;
    this.applyRiskFilter();
  }

  private applyRiskFilter() {
    this.RiskRows = this.RiskFilter
      ? this.RiskNotifications.filter(n => n.DeliveryStatus === this.RiskFilter)
      : [...this.RiskNotifications];
    this.cdr.markForCheck();
  }

  createRiskColumns(): Column[] {
    return [
      { key: 'LastAttemptDate', label: 'Last Attempt', type: 'date', SearchType: 'date', hidden: false },
      { key: 'ReserveOrderID', label: 'Reference', hidden: false },
      { key: 'Customer', label: 'Customer', type: 'select', SearchType: 'select', hidden: false },
      { key: 'FuturesMonth', label: 'Futures Month', type: 'select', SearchType: 'select', hidden: false },
      { key: 'OriginalWeightLbs', label: 'Weight (lbs)', type: 'number', hidden: false },
      { key: 'NotificationType', label: 'Type', type: 'select', SearchType: 'select', hidden: false },
      { key: 'DeliveryStatus', label: 'Status', type: 'select', SearchType: 'select', hidden: false, cellClass: (row) => 'delivery-' + String(row.DeliveryStatus).toLowerCase() },
      { key: 'Recipients', label: 'Recipients', hidden: false },
      { key: 'RetryCount', label: 'Retries', type: 'number', hidden: false },
      { key: 'NextRetryDate', label: 'Next Retry', type: 'date', hidden: false },
      { key: 'ErrorMessage', label: 'Error', hidden: false },
      { key: 'TriggeredByName', label: 'Triggered By', type: 'select', SearchType: 'select', hidden: false },
    ];
  }

  selectContractFilter(name: string | null) {
    this.ContractFilter = this.ContractFilter === name ? null : name;
    this.applyContractFilter();
  }

  private applyContractFilter() {
    this.PositionRows = this.ContractFilter
      ? this.AllRows.filter(r => r.ContractStatusName === this.ContractFilter)
      : [...this.AllRows];
    this.cdr.markForCheck();
  }

  openPosition(row: PositionRow) {
    this.SelectedPosition = row;
    this.SelectedReserveLines = row.ReserveOrderID == null ? [row] : this.AllRows.filter(r => r.ReserveOrderID === row.ReserveOrderID);
    this.cdr.markForCheck();
  }

  openNewContract() {
    const ref = this.pulldownService.open(FuturesContractFormComponent, {
      data: {},
      title: 'New Futures Contract',
      width: '70vw',
      maxWidth: '96vw',
      top: '0',
      maxHeight: '92vh'
    });

    ref.afterClosed().pipe(take(1)).subscribe({
      next: async (id: number | null) => {
        await this.loadPositions();
        const created = id ? this.AllRows.find(r => r.Source === 'RMS' && r.FuturesPositionID === id) : null;
        if (created) this.openPosition(created);
      }
    });
  }

  async clearSelection() {
    this.SelectedPosition = null;
    this.SelectedReserveLines = [];
    await this.loadPositions();
  }

  exportPositions() {
    downloadCsv(this.AllRows.map(r => ({
      Reference: r.Reference,
      Customer: r.Customer,
      FuturesMonth: r.FuturesLabel,
      Exchange: r.ExchangeName,
      Contracts: r.Contracts,
      WeightLbs: r.WeightLbs,
      PricePerLb: r.Price,
      PriceUnit: r.PriceUnit,
      ContractStatus: r.ContractStatusName,
      Application: r.ApplicationDisplay,
      Terminal: r.TerminalAllocation,
      ContractValue: r.ContractValue != null ? Number(r.ContractValue.toFixed(2)) : null,
      FirstNoticeDay: r.FND,
      DaysToFND: r.DaysToFND,
      Position: r.PositionStatus,
      Trader: r.TraderName,
      PurchaseDate: r.PurchaseDate,
      LotNumber: r.InventoryID
    })), `futures-positions-${new Date().toISOString().substring(0, 10)}.csv`);
  }

  async exportAuditReport() {
    try {
      const rows = await lastValueFrom(this.futuresData.getAuditReport());
      downloadCsv(rows ?? [], `futures-audit-${new Date().toISOString().substring(0, 10)}.csv`);
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not export the audit report.');
    }
  }
}
